import React, { ReactNode, useRef } from 'react';

import { isPathMatches } from '~/components/app-header/utils/navigation-utils';
import {
  ChatInputComposer,
  ChatInputConfig,
  SubmitOptions,
} from '~/components/chat';
import { useIsGeneratedQuestion } from '~/hooks/use-is-generated-question/useIsGeneratedQuestion';
import {
  POSTHOG_TRACKING_SOURCES,
  TrackingMetadata,
} from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useReasonerParams } from '~/views/alfred/research/hooks/useReasonerParams';
import { postClonePublicConversation } from '~/views/alfred/research/services/conversations-service';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

export interface ChatInputProps {
  config?: ChatInputConfig;
  submitCallback?: (message?: string) => void;
  trackingMetadata?: TrackingMetadata;
  /** Applied to the composer `<form>` (e.g. width overrides in drawers). */
  composerClassName?: string;
  usageIndicator?: ReactNode;
}

export const ChatDeepAnalysis = ({
  config,
  submitCallback,
  trackingMetadata,
  composerClassName,
  usageIndicator,
}: ChatInputProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const multiSubmitQueueRef = useRef(Promise.resolve());
  const {
    trackAlfredQuerySent,
    trackAlfredShare,
    trackAlfredConversationStarted,
  } = usePosthogEvents();
  const isAutoGenerated = useIsGeneratedQuestion(config?.suggestedValue);

  const { conversationId, setReasonerParams } = useReasonerParams();

  const {
    activeConversation,
    postQuestion,
    abortRequest,
    clonePublicConversation,
    appendNewConversation,
    analysisMode,
  } = useChat(state => ({
    activeConversation: state.getActiveConversation(conversationId),
    postQuestion: state.postQuestion,
    abortRequest: state.abortRequest,
    clonePublicConversation: state.clonePublicConversation,
    appendNewConversation: state.appendNewConversation,
    analysisMode: state.analysisMode,
  }));

  const isAlfredPage = isPathMatches(['alfred']);
  const lastTask = activeConversation?.tasks?.at(-1);
  const lastState = lastTask?.states?.at(-1);
  const isLoading =
    isAlfredPage &&
    !!conversationId &&
    (!!lastState?.next || activeConversation?.status === 'Processing');
  const isChatDisabled =
    config?.isCustomizedDisabled || lastState?.status === 'Fatal';

  const submitHandler = async (
    inputValue: string,
    onSubmitCallbackHandler: () => void,
    options?: SubmitOptions
  ) => {
    const question = inputValue.trim();
    const isPromptRefined = options?.isPromptRefined;
    const shouldStartNewConversation = options?.startNewConversation;

    if (isLoading || isChatDisabled || !question) {
      return;
    }

    const getConversationStartedSource = () => {
      const { newConversationOrigin } = useChat.getState();

      return newConversationOrigin === 'side_menu'
        ? POSTHOG_TRACKING_SOURCES.ALFRED_SIDE_MENU
        : null;
    };

    const queuedConversationStartedSource = shouldStartNewConversation
      ? getConversationStartedSource()
      : null;

    const submitQuestion = async () => {
      const captureQuerySent = (sentConversationId?: string) => {
        if (trackingMetadata?.source) {
          trackAlfredQuerySent({
            source: trackingMetadata.source,
            type: analysisMode,
            via: trackingMetadata.via,
            isAutoGenerated,
            value: question,
            conversationId: sentConversationId,
            isPromptRefined,
          });
        } else if (isAlfredPage) {
          trackAlfredQuerySent({
            source: POSTHOG_TRACKING_SOURCES.ALFRED,
            type: analysisMode,
            isAutoGenerated,
            value: question,
            conversationId: sentConversationId,
            isPromptRefined,
          });
        }
      };

      if (
        !shouldStartNewConversation &&
        (activeConversation?.access_level === 'public' ||
          (!activeConversation && conversationId))
      ) {
        const response = await postClonePublicConversation(conversationId);

        if (!response.error) {
          const newConversationId = response.data.conversation_id;

          trackAlfredShare(POSTHOG_TRACKING_SOURCES.ALFRED, 'continue');

          if (!config?.alfredEverywhere) {
            setReasonerParams({
              conversationId: newConversationId,
            });
          }

          const conversation = await clonePublicConversation(newConversationId);

          if (conversation) {
            appendNewConversation(conversation);
          }

          await postQuestion({
            question,
            conversationId:
              isAlfredPage && newConversationId ? newConversationId : undefined,
          });

          captureQuerySent(newConversationId);
          submitCallback?.();
          onSubmitCallbackHandler();

          return;
        }

        return;
      }

      const targetConversationId =
        shouldStartNewConversation || !isAlfredPage
          ? undefined
          : conversationId;
      const newConversationId = await postQuestion({
        question,
        conversationId: targetConversationId,
      });

      if (
        isAlfredPage &&
        newConversationId &&
        newConversationId !== conversationId
      ) {
        const { setNewConversationOrigin } = useChat.getState();
        const conversationStartedSource = shouldStartNewConversation
          ? queuedConversationStartedSource
          : getConversationStartedSource();

        trackAlfredConversationStarted(conversationStartedSource);

        setNewConversationOrigin(null);
      }

      captureQuerySent(newConversationId ?? conversationId);

      if (newConversationId && !config?.alfredEverywhere) {
        setReasonerParams({
          conversationId: newConversationId,
        });
      }

      submitCallback?.();
      onSubmitCallbackHandler();
    };

    if (shouldStartNewConversation) {
      multiSubmitQueueRef.current = multiSubmitQueueRef.current
        .then(submitQuestion)
        .catch(() => undefined);
      await multiSubmitQueueRef.current;
      return;
    }

    await submitQuestion();
  };

  const onSubmit = (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLTextAreaElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void,
    options?: SubmitOptions
  ) => {
    event.preventDefault();
    submitHandler(inputText, onSubmitCallbackHandler, options);
  };

  const onKeyDown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  return (
    <ChatInputComposer
      ref={inputRef}
      className={composerClassName}
      handlers={{
        onSubmit,
        onKeyDown,
        onSelect: (_item, clear) => {
          clear();
        },
        onAbort: () => abortRequest(conversationId),
      }}
      config={{
        ...config,
        isCustomizedDisabled: isChatDisabled,
        isLoading,
      }}
      trackingMetadata={trackingMetadata}
      promptRefinementContext={conversationId ? { conversationId } : undefined}
      usageIndicator={usageIndicator}
    />
  );
};
